<template>
  <div>
    <div class="initiative-events">
      <create-comment
        :mentionable-users="usersInScope"
        ref="commentEditor"
        :errors="commentErrors"
        :org-id="initiative.organizationId"
        :focus-add-comment-on-load="focusAddCommentOnLoad"
        :is-saving="isCreatingComment"
        :entity-type="ENTITY_TYPE.INITIATIVE"
        :entity="initiative"
        :show-subscribers="true"
        @save="postNewComment"
        @reset-errors="commentErrors = null"
      />
    </div>
    <!--Events-->
    <with-gallery :update-watcher="events">
      <div class="initiative-event-list">
        <initiative-event
          v-for="event in eventsToShow"
          :key="event.id"
          :event="event"
          :initiative="initiative"
          :mentionable-users="usersInScope"
          @update="updateEvent($event)"
          @delete="deleteEvent($event)"
          @awarded="refreshEvents"
          @reply="replyToEvent(event)"
        ></initiative-event>
      </div>
    </with-gallery>
  </div>
</template>
<script>
import CreateComment from '@/components/CreateComment'
import InitiativeEvent from '@/components/initiative/InitiativeEvent'
import WithGallery from '@/components/WithGallery'
import { COMMENT_FEED } from '@/constants/activity'
import { CUSTOM_EVENT_ACTION } from '@/constants/analytics'
import { MIME_TYPE } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { INITIATIVE_EVENT_TYPE } from '@/constants/initiative'
import { DEFAULT_ERROR } from '@/constants/messages'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import focusEventNode from '@/utils/focusEventNode'
import replyAsComment from '@/utils/replyAsComment'
import showAPIError from '@/utils/showAPIError'
import trackEventActivity from '@/utils/trackEventActivity'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '../analytics'

/**
 * This component shows list of comments posted on initiative.
 * It also gives an option to post a comment
 */
export default {
  name: 'InitiativeDiscussion',
  props: ['initiative', 'focusAddCommentOnLoad', 'query', 'selectedActivityFilter'],
  mixins: [MembershipsMixin, EventBusMixin],
  components: {
    CreateComment,
    InitiativeEvent,
    WithGallery
  },
  data() {
    return {
      commentErrors: null,
      events: [],
      isCreatingComment: false,
      ENTITY_TYPE
    }
  },
  computed: {
    eventToFocus() {
      return this.query && this.query.event
    },
    usersInScope() {
      return this.getUsersInScope(this.initiative.teamId, this.initiative.organizationId)
    },
    teamId() {
      return this.initiative.teamId
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    user() {
      return this.$store.getters.user
    },
    eventsToShow() {
      if (this.selectedActivityFilter?.id === COMMENT_FEED) {
        const commentEvents = [INITIATIVE_EVENT_TYPE.COMMENT, INITIATIVE_EVENT_TYPE.AWARD]
        return this.events.filter(x => commentEvents.includes(x.type))
      }
      return this.events
    }
  },
  async mounted() {
    const orgId = this.initiative.organizationId
    this.updateMemberships(orgId, this.team.id, this.team.isPrivate)
    // perform the event action (if any) before refresh, so we don't need to make another refresh call
    const actionDetails = await this.performEventAction()
    await this.refreshEvents()
    if (actionDetails) {
      this.trackAction(actionDetails)
    }
    this.$nextTick(async () => {
      this.eventToFocus && focusEventNode(this.eventToFocus)
    })
    this.listenToEventBus(events.INITIATIVE_UPDATED, this.onInitiativeUpdate)
    this.listenToEventBus('INITIATIVE_IS_CLOSED_CHANGED', this.refreshEvents)
    this.listenToEventBus('COMMENT_EDITED', this.onCommentChange)
    this.listenToEventBus('COMMENT_DELETED', this.onCommentChange)
    this.listenToEventBus('COMMENT_ADDED', this.onCommentChange)
  },
  methods: {
    ...mapActions(['getGoal', 'getInitiativeEvents', 'createInitiativeEvent']),
    async postNewComment(comment) {
      if (comment) {
        try {
          this.isCreatingComment = true
          const event = await this.createInitiativeEvent({
            id: this.initiative.id,
            comment,
            commentType: MIME_TYPE.HTML,
            orgId: this.initiative.organizationId,
            actorId: this.user.id
          })
          this.$refs.commentEditor.$refs.editor.setData(null)
          this.setEvents([event, ...this.events])
          this.commentErrors = null
          EventBus.$emit(events.COMMENT_ADDED, { type: 'initiative', value: this.initiative })
          EventBus.$emit('ADD_TOAST', { message: 'Comment has been posted successfully' })
        } catch (e) {
          let errors = R.path(['response', 'data', 'detail', 'comment'], e)
          if (!errors) {
            const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
            errors = [message]
          }
          this.commentErrors = errors
        }
        this.isCreatingComment = false
        trackEvent('Activity', 'New Comment', 'Initiative')
      }
    },
    setEvents(events) {
      this.events = events
    },
    updateEvent(event) {
      const eventIndex = this.events.findIndex(x => x.id === event.id)
      this.setEvents(R.assocPath([eventIndex], event, this.events))
      EventBus.$emit('COMMENT_EDITED', { type: 'initiative', value: this.initiative })
    },
    deleteEvent(event) {
      this.setEvents(this.events.filter(ev => ev.id !== event.id))
      EventBus.$emit('COMMENT_DELETED', { type: 'initiative', value: this.initiative })
    },
    async refreshEvents() {
      const events = await this.getInitiativeEvents({
        id: this.initiative.id,
        orgId: this.initiative.organizationId
      })
      this.setEvents(events)
    },
    onCommentChange({ type, value: initiative }) {
      if (type === 'initiative' && initiative.id === this.initiative.id) {
        this.refreshEvents()
      }
    },
    onInitiativeUpdate(initiative) {
      if (initiative.id === this.initiative.id) {
        this.refreshEvents()
      }
    },
    async performEventAction() {
      if (!this.query) {
        return null
      }
      const eventId = parseInt(this.query.event)
      const action = this.query.action
      const signature = this.query.signature
      const reactionType = this.query.reaction_type
      if (eventId && action && signature && reactionType) {
        const url = urlManager.initiativeEventDetails({
          orgId: this.initiative.organizationId,
          initiativeId: this.initiative.id,
          eventId: eventId
        })
        try {
          await service.client.post(url, {
            action: action,
            reaction_type: reactionType,
            signature: signature
          })
          return { eventId, action, reactionType }
        } catch (e) {
          showAPIError(e)
        }
      }
      return null
    },
    trackAction({ eventId, action }) {
      const searchParams = new URL(document.location).searchParams
      const medium = searchParams.get('mtm_medium')
      const event = this.events.find(ev => ev.id === eventId)
      trackEventActivity(event, CUSTOM_EVENT_ACTION[action], medium)
    },
    replyToEvent(event) {
      replyAsComment(event, this.$store, this.$refs.commentEditor)
    }
  },
  watch: {
    initiative(before, after) {
      if (before && after && before.id !== after.id) {
        this.refreshEvents()
      }
    },
    async eventToFocus(eventToFocus) {
      if (eventToFocus) {
        const event = this.events.find(x => x.id === +eventToFocus)
        if (!event) {
          await this.refreshEvents()
        }
        this.$nextTick(() => focusEventNode(eventToFocus))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.initiative-event-list,
.initiative-events {
  margin-bottom: 24px;
}

@include media-breakpoint-down(md) {
  .initiative-event-list,
  .initiative-events {
    padding-left: 4px;
    margin-top: 8px !important;
    margin-bottom: 0;
  }
}
</style>
